/**
 * FEAT-106 / S-425 — what the SPA does with a tenant's status.
 *
 * GET /tenants/me is the one route a suspended tenant may still call; its
 * `status` decides whether the app or the suspended notice renders. Any other
 * route answers 403 `tenant_suspended`, which tells an open tab to re-read
 * /tenants/me (App.tsx's query cache).
 */
import { apiErrorPayload } from "./api-error";

export type TenantGateState = "active" | "suspended";

/** `purged` (a purge in progress) shows the same notice as `suspended`. */
export function tenantGateState(row: { status?: string | null } | null | undefined): TenantGateState {
  const status = row?.status;
  return status === "suspended" || status === "purged" ? "suspended" : "active";
}

export function isTenantSuspendedError(err: unknown): boolean {
  return apiErrorPayload(err).error === "tenant_suspended";
}

/** "15 January 2027", or null when there is no date. */
export function formatKeptUntil(purgeAfter: string | null | undefined): string | null {
  if (!purgeAfter) return null;
  const d = new Date(purgeAfter);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

export function suspendedNoticeText(purgeAfter: string | null | undefined): string {
  const until = formatKeptUntil(purgeAfter);
  const kept = until
    ? `Your articles, documents and images are kept until ${until}.`
    : "Your articles, documents and images are kept for now.";
  return `This account is suspended. ${kept} To get a copy or reactivate the account, contact Nagareru.`;
}

/** The platform admin's tenant list: "Active", or "Suspended · kept until …". */
export function tenantStatusLabel(row: {
  status?: string | null;
  purge_after?: string | null;
  tenant_type?: string | null;
}): string {
  if (row.status === "purged") return "Purging";
  // Test Organization 1 and B2X Marketing: the API refuses to suspend them.
  if (row.tenant_type === "admin" || row.tenant_type === "internal") return "Active · internal";
  if (row.status !== "suspended") return "Active";
  const until = formatKeptUntil(row.purge_after);
  return until ? `Suspended · kept until ${until}` : "Suspended";
}
